<script setup lang="ts">
import type { Ref } from "vue"
import type { ISelectItem } from "@/types"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { computed, ref, watchEffect } from "vue"
import { z } from "zod"
import { useGetAlbum, useUpdateAlbum } from "@/api"
import { Alert, Button, Form, FormSection, Icon, Input, Select } from "@/components/ui"
import { useRouteParam, useTranslation } from "@/composables"
import { ValidationException } from "@/http"
import { Album } from "@/models"
import { useEventVaultStore } from "@/stores/eventVaultStore"

const emit = defineEmits<{
  success: [album: Album]
  error: [err?: unknown]
}>()

const eventStore = useEventVaultStore()
const albumId = useRouteParam("albumId", "/event/[id]/album/[albumId]/edit")
const { t } = useTranslation()

const validationErrors: Ref<Record<string, any>> = ref({})

const { data: existingAlbum, isLoading: isLoadingAlbum } = useGetAlbum(eventStore.eventVaultId, albumId, { include: ["settings"] })

const validationSchema = toTypedSchema(
  z
    .object({
      privacy: z.enum(["public", "protected", "private"]),
      passcode: z.optional(z.string()),
    })
    .refine(
      (fields) => {
        return !(fields.privacy === "protected" && !/^\d{4,10}$/.test(fields.passcode ?? ""))
      },
      {
        message: t("album_settings.privacy.validation.passcode_required"),
        path: ["passcode"],
      },
    ),
)

const { values, setFieldValue, handleSubmit } = useForm({
  validationSchema,
  initialValues: {
    privacy: "public",
    passcode: "",
  },
})

watchEffect(async () => {
  if (existingAlbum.value) {
    await initialiseFormValues(existingAlbum.value)
  }
})

async function initialiseFormValues(album: Album) {
  setFieldValue("privacy", album.isLocked ? "protected" : album.isHidden ? "private" : "public")
}

const isAlreadyProtected = computed(() => existingAlbum.value?.isLocked ?? false)

const { mutate, isPending } = useUpdateAlbum()

const submit = handleSubmit((values) => {
  if (!eventStore.eventVaultId) {
    return
  }

  const album: Album = new Album({
    ...existingAlbum.value,
    id: albumId.value,
    title: existingAlbum.value?.title ?? "",
    isLocked: values.privacy === "protected",
    isHidden: values.privacy === "private",
    passcode: values.passcode,
  })

  mutate({ album, eventVaultId: eventStore.eventVaultId }, {
    onSuccess: (album: Album) => emit("success", album),
    onError: (e: unknown) => {
      if (e instanceof ValidationException)
        validationErrors.value = e.validationErrors
      emit("error", e)
    },
    onSettled() {
      window.scrollTo({ top: 0, behavior: "smooth" })
    },
  })
})

const privacyOptions: Array<ISelectItem> = [
  { label: t("public"), value: "public" },
  { label: t("protected"), value: "protected" },
  { label: t("private"), value: "private" },
]
</script>

<template>
  <Transition name="gpx-fade" mode="out-in">
    <div v-if="isLoadingAlbum" class="flex flex-col gap-6 py-6">
      <div>
        <div class="gpx-skeleton w-48 h-4 mb-2" />
        <div class="gpx-skeleton w-full h-12" />
      </div>
      <div>
        <div class="gpx-skeleton w-48 h-4 mb-2" />
        <div class="gpx-skeleton w-full h-12" />
      </div>
      <div>
        <div class="gpx-skeleton w-48 h-4 mb-2" />
        <div class="gpx-skeleton w-full h-12" />
      </div>
      <div>
        <div class="gpx-skeleton w-48 h-4 mb-2" />
        <div class="gpx-skeleton w-full h-12" />
      </div>
    </div>
    <Form v-else @submit.prevent="submit">
      <FormSection :title="t('album_settings.privacy.privacy_title')" icon="FolderLock">
        <template #description>
          <p class="mb-4">
            {{ t("album_settings.privacy.privacy_description") }}
          </p>
          <dl>
            <dt class="font-bold mb-1">
              {{ t("public") }}
            </dt>
            <dd class="mb-2">
              {{ t("album_create.public_description") }}
            </dd>
            <dt class="font-bold mb-1">
              {{ t("private") }}
            </dt>
            <dd class="mb-2">
              {{ t("album_create.private_description") }}
            </dd>
            <dt class="font-bold mb-1">
              {{ t("protected") }}
            </dt>
            <dd class="mb-2">
              {{ t("album_create.protected_description") }}
            </dd>
          </dl>
        </template>

        <Select
          name="privacy" :items="privacyOptions" :label="t('album_settings.privacy.privacy_label')"
        />

        <div>
          <Alert v-if="isAlreadyProtected && values.privacy === 'protected'" variant="success" :closable="false" class="mb-3" data-test="passcode-saved-confirmation">
            {{ t("album_settings.privacy.passcode_saved_confirmation") }}
          </Alert>

          <Input
            name="passcode" :label="t('album_settings.privacy.passcode_label')" type="password"
            :disabled="values.privacy !== 'protected'" :hint="t('album_settings.privacy.passcode_hint')"
          />
        </div>
      </FormSection>

      <template #actions>
        <div class="flex gap-4 items-center justify-end">
          <Button variant="primary" size="lg" :disabled="isPending">
            {{ t("submit") }}
          </Button>
          <Icon v-if="isPending" icon="Loading" />
        </div>
      </template>
    </Form>
  </Transition>
</template>
